// redux/services/buildingService/building.Services.js
import { baseApi } from "../baseApi";

export const BuildingApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ======================
    // ADMIN BUILDING APIS
    // ======================

    // GET ALL BUILDINGS (Admin)
    getAdminBuildings: builder.query({
      query: ({
        page = 1,
        limit = 10,
        type = "",
        status = "",
        search = "",
      } = {}) =>
        `/buildings?page=${page}&limit=${limit}&type=${type}&status=${status}&search=${search}`,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: "Building", id })),
              { type: "Building", id: "LIST" },
            ]
          : [{ type: "Building", id: "LIST" }],
    }),

    // GET BUILDING BY ID (Admin)
    getAdminBuilding: builder.query({
      query: (id) => `/building/${id}`,
      providesTags: (result, error, id) => [{ type: "Building", id }],
    }),

    // CREATE BUILDING
    createBuilding: builder.mutation({
      query: (data) => ({
        url: "/building-create",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Building"],
    }),

    // UPDATE BUILDING
    updateBuilding: builder.mutation({
      query: ({ id, data }) => ({
        url: `/building-update/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Building", id },
        { type: "Building", id: "LIST" },
      ],
    }),

    // DELETE BUILDING
    deleteBuilding: builder.mutation({
      query: (id) => ({
        url: `/building-delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Building", id },
        { type: "Building", id: "LIST" },
      ],
    }),

    // UPDATE BUILDING STATUS
    updateBuildingStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/building-status/${id}`,
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Building", id },
        { type: "Building", id: "LIST" },
      ],
    }),

    // UPLOAD BUILDING IMAGES
    uploadBuildingImages: builder.mutation({
      query: ({ id, data }) => ({
        url: `/building-images/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "Building", id }],
    }),

    // DELETE BUILDING IMAGE
    deleteBuildingImage: builder.mutation({
      query: (id) => ({
        url: `/building-image/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [{ type: "Building", id }],
    }),

    // ======================
    // ADMIN BOOKING APIS
    // ======================

    // GET ALL BOOKINGS (Admin)
    getAdminBookings: builder.query({
      query: ({
        page = 1,
        limit = 10,
        status = "",
        building_id = "",
        search = "",
      } = {}) =>
        `/bookings?page=${page}&limit=${limit}&status=${status}&building_id=${building_id}&search=${search}`,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: "Booking", id })),
              { type: "Booking", id: "LIST" },
            ]
          : [{ type: "Booking", id: "LIST" }],
    }),

    // UPDATE BOOKING STATUS
    updateBookingStatus: builder.mutation({
      query: ({ id, status, admin_remarks }) => ({
        url: `/booking-status/${id}`,
        method: "PUT",
        body: { status, admin_remarks },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Booking", id },
        { type: "Booking", id: "LIST" },
      ],
    }),

    // GET BOOKING STATISTICS
    getBookingStatistics: builder.query({
      query: () => "/booking-statistics",
      providesTags: ["Booking"],
    }),

    // ======================
    // FRONTEND BUILDING APIS
    // ======================

    // GET BUILDINGS (Frontend)
    getBuildings: builder.query({
      query: ({
        page = 1,
        limit = 10,
        type = "",
        city = "",
        search = "",
      } = {}) =>
        `/buildings?page=${page}&limit=${limit}&type=${type}&city=${city}&search=${search}`,
      providesTags: ["Building"],
    }),

    // GET BUILDING BY SLUG
    getBuildingBySlug: builder.query({
      query: (slug) => `/building/${slug}`,
      providesTags: (result, error, slug) => [{ type: "Building", slug }],
    }),

    // GET BUILDING AVAILABILITY
    getBuildingAvailability: builder.query({
      query: ({ id, start_date, end_date }) =>
        `/building-availability/${id}?start_date=${start_date}&end_date=${end_date}`,
      providesTags: (result, error, { id }) => [{ type: "Building", id }],
    }),

    // CREATE BOOKING (Frontend)
    createBooking: builder.mutation({
      query: (data) => ({
        url: "/building-book",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Booking"],
    }),

    // GET BOOKING BY ID
    getBooking: builder.query({
      query: (id) => `/booking/${id}`,
      providesTags: (result, error, id) => [{ type: "Booking", id }],
    }),

    // GET BOOKINGS BY USER
    getBookingsByUser: builder.query({
      query: (user_id) => `/bookings-user/${user_id}`,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: "Booking", id })),
              { type: "Booking", id: "LIST" },
            ]
          : [{ type: "Booking", id: "LIST" }],
    }),

    // CANCEL BOOKING
    cancelBooking: builder.mutation({
      query: (id) => ({
        url: `/booking-cancel/${id}`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, id) => [{ type: "Booking", id }],
    }),
  }),
});

export const {
  // Admin Building
  useGetAdminBuildingsQuery,
  useGetAdminBuildingQuery,
  useCreateBuildingMutation,
  useUpdateBuildingMutation,
  useDeleteBuildingMutation,
  useUpdateBuildingStatusMutation,
  useUploadBuildingImagesMutation,
  useDeleteBuildingImageMutation,

  // Admin Booking
  useGetAdminBookingsQuery,
  useUpdateBookingStatusMutation,
  useGetBookingStatisticsQuery,

  // Frontend Building
  useGetBuildingsQuery,
  useGetBuildingBySlugQuery,
  useGetBuildingAvailabilityQuery,

  // Frontend Booking
  useCreateBookingMutation,
  useGetBookingQuery,
  useGetBookingsByUserQuery,
  useCancelBookingMutation,
} = BuildingApi;

export default BuildingApi;